/**
 * The frame loop's one call into the mission/dogfight layer: pushes the
 * mission HUD out, and fires `onMissionEnd`/`onDogfightEnd` exactly once.
 *
 * Split out of `flight-scene.ts` (the merged instant-dogfight-debrief pass
 * pushed it past the 400-line gate) rather than folded into `mission-hud.ts`:
 * this owns the *reporting* — when to fire, and exactly once — while
 * `mission-hud.ts`'s `debriefIfDecided`/`missionHudFrom` stay pure functions
 * of a `MissionRun`.
 */

import type { EncounterRunner, MissionRunner } from "./sim-runner.ts";
import { isOver } from "@red-opus/engine";
import type { FlightSceneCallbacks } from "./flight-scene-api.ts";
import { debriefIfDecided, missionHudFrom } from "./mission-hud.ts";

/** The slice of `flight-scene.ts`'s `SceneState` this reporting step reads and latches. */
export interface MissionReportState {
  readonly runner: EncounterRunner;
  /** The same object as `runner` when a mission is being flown; `null` for the instant dogfight. */
  readonly missionRunner: MissionRunner | null;
  /** True only for the genuine instant dogfight — see `flight-scene.ts`'s own doc on the field. */
  readonly isDogfight: boolean;
  missionEnded: boolean;
  dogfightEnded: boolean;
  /** REAL NAVIGATION withholds the waypoint cue (AD-013) — presentation only. */
  readonly hideWaypoint: boolean;
}

/**
 * A decided `MissionRun` freezes itself (`stepMissionRun`), so the aeroplane
 * sits still under the debrief instead of flying on unattended. The instant
 * dogfight has no `MissionRun` to read, so it gets its own branch:
 * `onDogfightEnd` fires once, the tick the bare encounter's own
 * `EncounterStatus` is decided — `sim-runner.ts`'s `encounterDecided` freezes
 * that runner the same tick, the same way a mission's own freeze already works.
 */
/**
 * G38's RETURN TO BASE: end the sortie on the player's say-so rather than on the
 * run's own verdict, and hand the debriefing out through the same door.
 *
 * **Why it goes through this file's latches rather than round them.** The route
 * could set its own `debrief` signal and raise the card, and the card would
 * appear — but `missionEnded` would still be `false`, and that flag is what
 * `scene-audio.ts`'s `silentFlight` reads. The engine would go on droning behind
 * the score sheet, which is the exact defect that function exists to have fixed.
 * So there is one place a sortie ends, and this is a second way *into* it, not a
 * second copy of it.
 *
 * **The outcome takes care of itself.** `missionDebrief` is called directly
 * rather than through `debriefIfDecided`, whose whole job is to refuse while the
 * run says `inProgress` — which is precisely the case here. An undecided run
 * comes out of `debriefHeadline` as **MISSION BROKEN OFF**, a line that was
 * already written and until now unreachable, and `simendOutcome`'s last branch
 * already documents an in-air ending as `safeReturnBase` — *"a sortie that
 * `/fly` ends in the air is reported as the flight home it would have been"*.
 * Neither needed a new outcome member; both were waiting for a caller.
 *
 * Idempotent, because the row that fires it is on a board that can be reopened,
 * and because a sortie that ended by itself while the board was up must not be
 * credited twice.
 */
export function endSortie(state: MissionReportState, callbacks: FlightSceneCallbacks): void {
  if (state.missionRunner) {
    if (state.missionEnded) return;
    state.missionEnded = true;
    callbacks.onMissionEnd?.(state.missionRunner.mission());
    return;
  }
  if (!state.isDogfight || state.dogfightEnded) return;
  state.dogfightEnded = true;
  callbacks.onDogfightEnd?.(state.runner.encounter());
}

export function reportMission(state: MissionReportState, callbacks: FlightSceneCallbacks): void {
  if (state.missionRunner) {
    const run = state.missionRunner.mission();
    callbacks.onMission?.(missionHudFrom(run, { hideWaypoint: state.hideWaypoint }));
    if (state.missionEnded || !debriefIfDecided(run)) return;
    state.missionEnded = true;
    callbacks.onMissionEnd?.(run);
    return;
  }
  if (!state.isDogfight || state.dogfightEnded) return;
  const encounter = state.runner.encounter();
  if (!isOver(encounter.status)) return;
  state.dogfightEnded = true;
  callbacks.onDogfightEnd?.(encounter);
}
